import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as api from '../api/api';
import { clearAuthTokens, setAuthTokens } from '../lib/authStorage';
import {
  getPermissionsForRole,
  normalizeRole,
  roleHasPermission,
  roleHasAnyPermission,
} from '../config/rbac';

const AuthContext = createContext(null);

function normalizeAdmin(data) {
  const admin = data?.admin || data;
  if (!admin || typeof admin !== 'object') return null;
  const role = normalizeRole(admin.role) || admin.role;
  const permissions =
    Array.isArray(admin.permissions) && admin.permissions.length > 0
      ? admin.permissions
      : getPermissionsForRole(role);
  return {
    ...admin,
    role,
    brandId: admin.brandId || (role === 'brand_admin' ? admin.adminId || admin.id : undefined),
    profilePicture: admin.profilePicture || admin.avatarUrl || admin.profileImageUrl,
    permissions,
  };
}

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const role = admin?.role || null;
  const permissions = useMemo(
    () => admin?.permissions ?? getPermissionsForRole(role),
    [admin?.permissions, role],
  );

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.fetchMe();
      setAdmin(normalizeAdmin(data));
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const login = useCallback(async (email, password, options = {}) => {
    const data = await api.loginAdmin({ email, password, ...options });
    if (data?.accessToken && data?.refreshToken) {
      setAuthTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
    }
    const next = normalizeAdmin(data);
    setAdmin(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logoutAdmin();
    } finally {
      clearAuthTokens();
      setAdmin(null);
    }
  }, []);

  const hasPermission = useCallback(
    (permission) => roleHasPermission(role, permission),
    [role],
  );

  const hasAnyPermission = useCallback(
    (perms) => roleHasAnyPermission(role, perms),
    [role],
  );

  const hasRole = useCallback((roles) => {
    if (!role) return false;
    const list = Array.isArray(roles) ? roles : [roles];
    return list.includes(role);
  }, [role]);

  const value = useMemo(
    () => ({
      admin,
      role,
      permissions,
      loading,
      isAuthenticated: Boolean(admin),
      login,
      logout,
      reloadSession: loadSession,
      hasPermission,
      hasAnyPermission,
      hasRole,
    }),
    [admin, role, permissions, loading, login, logout, loadSession, hasPermission, hasAnyPermission, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
